import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * Global search — a single multi-entity command-palette endpoint spanning
 * People, Deals, and Properties.
 *
 * `GET /search?q=<text>&limit=<n>` → `{ data: SearchResult[] }`.
 *
 * Results are grouped/ranked server-side (People → Deals → Properties, each
 * group capped ~6). The endpoint may not be deployed in every environment yet;
 * the palette degrades cleanly (no results, no crash) when it 404s.
 */

export type SearchResultType = "person" | "deal" | "property";

export interface SearchResult {
  id: string;
  type: SearchResultType;
  /** Primary line. */
  name: string;
  /** Secondary line, e.g. "Contract · $13.2M · Brennan Family Trust". */
  sub: string;
  /** Navigation target, e.g. /contacts/:id, /deals/:id, /properties/:id. */
  href: string;
}

export const searchApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "searchApi",
    baseUrl: "/api/search",
  }),
  reducerPath: "searchApi",
  endpoints: (builder) => ({
    globalSearch: builder.query<SearchResult[], { q: string; limit?: number }>({
      query: ({ q, limit }) => {
        const params = new URLSearchParams();
        params.append("q", q);
        if (limit) params.append("limit", limit.toString());
        return `?${params.toString()}`;
      },
      transformResponse: (response: { data?: SearchResult[] }) =>
        response?.data ?? [],
      // Degrade gracefully pre-deploy: a 404/error yields no results, no throw.
      transformErrorResponse: () => undefined,
      keepUnusedDataFor: 30,
    }),
  }),
});

export const { useGlobalSearchQuery, useLazyGlobalSearchQuery } = searchApi;
